/**
 * BLRRD paint engine — per-dab stamp operations (Phase 4.C.paint.1,
 * Wave 9.1).
 *
 * Three pure functions that stamp a single brush dab into a Float32 RGBA
 * layer buffer (ACEScg-linear, NON-PREMULTIPLIED straight alpha — matching
 * the pixel-layer model in src/layers/model.js). All three:
 *
 *   • take a cached brush mask (built by brush-mask.js) plus an *effective*
 *     dab diameter that may differ from the cached mask size (pressure-
 *     driven size changes — dossier §3.2). The mask is bilinear-sampled at
 *     the cached resolution and re-projected onto the effective footprint,
 *     so we never regenerate the mask per pixel-size change.
 *   • clip to the buffer bounds (a dab at the edge paints only the in-
 *     bounds portion — no wrap, no throw).
 *   • return the integer dirty bbox actually touched so paint-core can
 *     accumulate the region for LODScheduler.requestFull({region}).
 *
 * Compositing math — straight-alpha Porter-Duff "over" (Porter & Duff,
 * "Compositing Digital Images", SIGGRAPH 1984). For source color `S` with
 * coverage `αs` over destination `D` with alpha `αd`:
 *
 *   αo      = αs + αd·(1 − αs)
 *   Co·αo   = S·αs + D·αd·(1 − αs)            (premultiplied accumulation)
 *   Co      = (S·αs + D·αd·(1 − αs)) / αo     (un-premultiply for storage)
 *
 * We keep buffers non-premultiplied (Option A in the dossier §2.1) to match
 * the existing pipeline invariant: the compositor (src/layers/compositor.js)
 * expects straight-alpha layer buffers.
 *
 * Provenance (clean-room): Porter-Duff 1984 (over operator), Hughes/van Dam
 * §10.5 (stamped-brush rendering). NO GIMP/GEGL/Krita/Photoshop source.
 *
 * @module src/pipeline/paint/brush-ops
 */

/* Numerical floor below which we treat resulting alpha as 0 to avoid
 * un-premultiply division blowing up. */
const ALPHA_EPS = 1e-7;

/**
 * Sample the cached brush mask at continuous cell coordinates (mx, my)
 * using bilinear interpolation with edge clamping. Cell centres sit at
 * integer indices 0…maskSize-1; a coordinate of (k) reads exactly cell k.
 * Coordinates outside [−0.5, maskSize−0.5] return 0 (the dab footprint
 * extends only over the mask's own extent — the mask already encodes the
 * radial 0-outside-disc cutoff for maskSize ≥ 2).
 *
 * @param {Float32Array} mask    cached mask, maskSize × maskSize
 * @param {number} maskSize
 * @param {number} mx            continuous cell x
 * @param {number} my            continuous cell y
 * @returns {number} coverage α ∈ [0, 1]
 */
function sampleMaskBilinear(mask, maskSize, mx, my) {
    /* Strict half-open footprint: a coordinate exactly on the outer rim
     * (±0.5 cell beyond the edge cells) is treated as outside so a 1-cell
     * mask paints exactly one pixel rather than smearing across the
     * floor/ceil-padded bbox. */
    if (mx <= -0.5 || mx >= maskSize - 0.5 || my <= -0.5 || my >= maskSize - 0.5) return 0;
    /* Clamp the interpolation footprint to valid cell indices. */
    let x0 = Math.floor(mx);
    let y0 = Math.floor(my);
    const tx = mx - x0;
    const ty = my - y0;
    let x1 = x0 + 1;
    let y1 = y0 + 1;
    if (x0 < 0) x0 = 0; if (x0 > maskSize - 1) x0 = maskSize - 1;
    if (y0 < 0) y0 = 0; if (y0 > maskSize - 1) y0 = maskSize - 1;
    if (x1 < 0) x1 = 0; if (x1 > maskSize - 1) x1 = maskSize - 1;
    if (y1 < 0) y1 = 0; if (y1 > maskSize - 1) y1 = maskSize - 1;
    const a00 = mask[y0 * maskSize + x0];
    const a10 = mask[y0 * maskSize + x1];
    const a01 = mask[y1 * maskSize + x0];
    const a11 = mask[y1 * maskSize + x1];
    const top = a00 + (a10 - a00) * tx;
    const bot = a01 + (a11 - a01) * tx;
    return top + (bot - top) * ty;
}

/**
 * Map a pixel-centre world coordinate to a continuous mask-cell coordinate
 * for a dab of `effSize` whose footprint starts at `origin = centre − r`.
 * `(worldCentre − origin) / effSize` ∈ [0,1) over the footprint; ×maskSize
 * scales to cell space; −0.5 shifts to cell-centre convention.
 *
 * @param {number} world     pixel-centre coordinate (x+0.5 or y+0.5)
 * @param {number} origin    footprint origin (cx − r or cy − r)
 * @param {number} effSize   footprint diameter
 * @param {number} maskSize
 * @returns {number} continuous mask-cell coordinate
 */
function worldToMaskCell(world, origin, effSize, maskSize) {
    if (effSize <= 0) return 0;
    return ((world - origin) / effSize) * maskSize - 0.5;
}

/**
 * Compute the integer pixel bbox a dab of `effSize` centred at (cx, cy)
 * touches, clipped to the buffer. Returns null when fully off-buffer.
 *
 * @returns {{x0:number,y0:number,x1:number,y1:number}|null} inclusive bounds
 */
function dabBounds(cx, cy, effSize, w, h) {
    const r = effSize / 2;
    let x0 = Math.floor(cx - r);
    let y0 = Math.floor(cy - r);
    let x1 = Math.ceil(cx + r);
    let y1 = Math.ceil(cy + r);
    if (x0 < 0) x0 = 0;
    if (y0 < 0) y0 = 0;
    if (x1 > w - 1) x1 = w - 1;
    if (y1 > h - 1) y1 = h - 1;
    if (x1 < x0 || y1 < y0) return null;
    return { x0, y0, x1, y1 };
}

/**
 * Convert an inclusive pixel bbox to the {x,y,w,h} region shape used by
 * the LOD scheduler. Returns null for an empty bbox.
 */
function boundsToRegion(b) {
    if (!b) return null;
    return { x: b.x0, y: b.y0, w: b.x1 - b.x0 + 1, h: b.y1 - b.y0 + 1 };
}

/**
 * Brush dab — straight-alpha Porter-Duff "over" of (fgColor, mask·flow)
 * into the layer buffer.
 *
 * @param {Float32Array} rgba    layer buffer (w·h·4, ACEScg straight-alpha)
 * @param {number} w
 * @param {number} h
 * @param {number} cx            dab centre x (image pixels, may be fractional)
 * @param {number} cy            dab centre y
 * @param {object} brush
 * @param {Float32Array} brush.mask
 * @param {number} brush.maskSize
 * @param {number} brush.effSize  effective footprint diameter (≈ brush.maskSize
 *                                unless pressure scales it)
 * @param {number[]} brush.color  [r, g, b] in ACEScg
 * @param {number} brush.flow     per-dab alpha multiplier ∈ [0, 1]
 * @returns {{x:number,y:number,w:number,h:number}|null} dirty region
 */
export function stampBrushDab(rgba, w, h, cx, cy, brush) {
    const { mask, maskSize, effSize, color, flow } = brush;
    const b = dabBounds(cx, cy, effSize, w, h);
    if (!b) return null;
    const f = flow == null ? 1 : (flow < 0 ? 0 : flow > 1 ? 1 : flow);
    if (f <= 0) return null;
    const sr = color[0], sg = color[1], sb = color[2];
    const r = effSize / 2;
    const ox = cx - r, oy = cy - r;

    for (let y = b.y0; y <= b.y1; y++) {
        const my = worldToMaskCell(y + 0.5, oy, effSize, maskSize);
        const row = y * w * 4;
        for (let x = b.x0; x <= b.x1; x++) {
            const mx = worldToMaskCell(x + 0.5, ox, effSize, maskSize);
            const cov = sampleMaskBilinear(mask, maskSize, mx, my);
            if (cov <= 0) continue;
            const as = cov * f;                 /* source coverage this dab */
            if (as <= 0) continue;
            const j = row + x * 4;
            const dr = rgba[j], dg = rgba[j + 1], db = rgba[j + 2], ad = rgba[j + 3];
            const ao = as + ad * (1 - as);      /* Porter-Duff over alpha */
            if (ao <= ALPHA_EPS) {
                rgba[j] = 0; rgba[j + 1] = 0; rgba[j + 2] = 0; rgba[j + 3] = 0;
                continue;
            }
            const invAo = 1 / ao;
            /* Premultiplied accumulation, then un-premultiply for storage. */
            rgba[j]     = (sr * as + dr * ad * (1 - as)) * invAo;
            rgba[j + 1] = (sg * as + dg * ad * (1 - as)) * invAo;
            rgba[j + 2] = (sb * as + db * ad * (1 - as)) * invAo;
            rgba[j + 3] = ao;
        }
    }
    return boundsToRegion(b);
}

/**
 * Pencil dab — identical to brush but the mask coverage is binary-
 * thresholded at α ≥ 0.5 (no anti-aliasing). Gives the pixel-art "every
 * output pixel is exactly the brush color or untouched" feel. The cached
 * Gaussian/plateau mask is reused; the threshold is applied on the way out.
 *
 * @returns {{x:number,y:number,w:number,h:number}|null} dirty region
 */
export function stampPencilDab(rgba, w, h, cx, cy, brush) {
    const { mask, maskSize, effSize, color, flow } = brush;
    const b = dabBounds(cx, cy, effSize, w, h);
    if (!b) return null;
    const f = flow == null ? 1 : (flow < 0 ? 0 : flow > 1 ? 1 : flow);
    if (f <= 0) return null;
    const sr = color[0], sg = color[1], sb = color[2];
    const r = effSize / 2;
    const ox = cx - r, oy = cy - r;

    for (let y = b.y0; y <= b.y1; y++) {
        const my = worldToMaskCell(y + 0.5, oy, effSize, maskSize);
        const row = y * w * 4;
        for (let x = b.x0; x <= b.x1; x++) {
            const mx = worldToMaskCell(x + 0.5, ox, effSize, maskSize);
            const cov = sampleMaskBilinear(mask, maskSize, mx, my);
            /* Binary threshold — no intermediate alpha (Pencil = no AA). */
            if (cov < 0.5) continue;
            const as = f;                       /* full coverage where painted */
            const j = row + x * 4;
            const dr = rgba[j], dg = rgba[j + 1], db = rgba[j + 2], ad = rgba[j + 3];
            const ao = as + ad * (1 - as);
            if (ao <= ALPHA_EPS) {
                rgba[j] = 0; rgba[j + 1] = 0; rgba[j + 2] = 0; rgba[j + 3] = 0;
                continue;
            }
            const invAo = 1 / ao;
            rgba[j]     = (sr * as + dr * ad * (1 - as)) * invAo;
            rgba[j + 1] = (sg * as + dg * ad * (1 - as)) * invAo;
            rgba[j + 2] = (sb * as + db * ad * (1 - as)) * invAo;
            rgba[j + 3] = ao;
        }
    }
    return boundsToRegion(b);
}

/**
 * Eraser dab — destination-out: multiply the layer's alpha by (1 − α_mask)
 * under the brush. RGB channels are left untouched (the colour is simply
 * revealed less as the alpha drops). For a fully-opaque layer this carves
 * transparency, letting the layers below (or the kernel base) show through
 * the compositor.
 *
 * @returns {{x:number,y:number,w:number,h:number}|null} dirty region
 */
export function stampEraserDab(rgba, w, h, cx, cy, brush) {
    const { mask, maskSize, effSize, flow } = brush;
    const b = dabBounds(cx, cy, effSize, w, h);
    if (!b) return null;
    const f = flow == null ? 1 : (flow < 0 ? 0 : flow > 1 ? 1 : flow);
    if (f <= 0) return null;
    const r = effSize / 2;
    const ox = cx - r, oy = cy - r;

    for (let y = b.y0; y <= b.y1; y++) {
        const my = worldToMaskCell(y + 0.5, oy, effSize, maskSize);
        const row = y * w * 4;
        for (let x = b.x0; x <= b.x1; x++) {
            const mx = worldToMaskCell(x + 0.5, ox, effSize, maskSize);
            const cov = sampleMaskBilinear(mask, maskSize, mx, my);
            if (cov <= 0) continue;
            const as = cov * f;
            if (as <= 0) continue;
            const j = row + x * 4;
            rgba[j + 3] *= (1 - as);
        }
    }
    return boundsToRegion(b);
}

/* Internal helpers exposed for the test gate. */
export { sampleMaskBilinear, worldToMaskCell, dabBounds, boundsToRegion, ALPHA_EPS };
